// composables/shoppingLists.ts
import { ref, computed, watch, onMounted } from 'vue'
import { createStore, get as idbGet, set as idbSet } from 'idb-keyval'
import { useVmStore } from '@/stores/vm'
import { storeToRefs } from 'pinia'
import type { ProductType } from '@/components/ProductTypes'

// Use a fresh database name so IndexedDB can auto-create the 'carts' store
const cartsStore = createStore('shopping-lists-db', 'carts')

const IDS_KEY      = 'cart_itemIds'
const NON_PRODUCTS_KEY = 'cart_nonProductItems'
const SELECTED_KEY = 'cart_selectedCart'

const selectedCart = ref<string>('')
let isInitialized = false
let watchersInitialized = false

async function initializeOnce() {
  if (isInitialized) return
  isInitialized = true
  
  try {
    // Load cart items and non-product items
    const [map, nonProductsMap] = await Promise.all([
      idbGet<Record<string, string[]>>(IDS_KEY, cartsStore),
      idbGet<Record<string, ProductType[]>>(NON_PRODUCTS_KEY, cartsStore)
    ])
    
    if (map || nonProductsMap) {
      console.log('Loaded cart items from IndexedDB:', { productIds: map, nonProducts: nonProductsMap })
      return { productIds: map, nonProducts: nonProductsMap }
    } else {
      console.log('No cart items found in IndexedDB')
    }
  } catch (error) {
    console.log('Error loading from IndexedDB:', error)
  }
  
  return null
}

export function useShoppingLists() {
  
  const vm = useVmStore()
  const { carts, products } = storeToRefs(vm)

  // Ensure we always have a default selection
  watch(
    () => carts.value.length,
    (len) => {
      if (len > 0 && !selectedCart.value) {
        selectedCart.value = carts.value[0].name
      }
    },
    { immediate: true }
  )

  const selectedCartItems = computed(() => {
    const cart = carts.value.find(c => c.name === selectedCart.value)
    return cart ? cart.items : []
  })

  function selectCart(name: string) {
    selectedCart.value = name
  }

  function addToCart(item: ProductType) {
    const shop = carts.value.find(c => c.name === 'Bag')
    if (shop) {
      const existingItem = shop.items.find(i => i.id === item.id)
      if (existingItem) {
        // If item exists, add the new item's quantity to the existing quantity
        const newQty = item.qty || 1
        existingItem.qty = (existingItem.qty || 0) + newQty
      } else {
        // If item doesn't exist, add it to cart
        shop.items.push({ ...item, qty: item.qty || 1 })
      }
    }
  }

  function deleteItem(id: string) {
    const cart = carts.value.find(c => c.name === selectedCart.value)
    if (cart) {
      cart.items = cart.items.filter(i => i.id !== id)
    }
  }

  function saveForLater(id: string) {
    const shop  = carts.value.find(c => c.name === 'Bag')
    const later = carts.value.find(c => c.name === 'Save for Later')
    if (shop && later) {
      // if already in Save for Later, just remove from Shopping Cart
      if (later.items.some(i => i.id === id)) {
        shop.items = shop.items.filter(i => i.id !== id)
        return
      }
      // otherwise, move it
      const it = shop.items.find(i => i.id === id)
      if (it) {
        later.items.push(it)
        shop.items = shop.items.filter(i => i.id !== id)
      }
    }
  }

  function bringBackToCart(id: string) {
    const shop  = carts.value.find(c => c.name === 'Bag')
    const later = carts.value.find(c => c.name === 'Save for Later')
    if (shop && later) {
      // if already in Shopping Cart, just remove from Save for Later
      if (shop.items.some(i => i.id === id)) {
        later.items = later.items.filter(i => i.id !== id)
        return
      }
      // otherwise, move it
      const it = later.items.find(i => i.id === id)
      if (it) {
        shop.items.push(it)
        later.items = later.items.filter(i => i.id !== id)
      }
    }
  }

  onMounted(async () => {
   // console.log('onMounted called')
    
    // Initialize default carts if empty
    if (carts.value.length === 0) {
      carts.value.push(
        { name: 'Bag',   items: [] },
        { name: 'Save for Later',  items: [] }
      )
    }

    // Apply hydrated data if available
    const savedData = await initializeOnce()
    if (savedData) {
      carts.value.forEach(cart => {
        const items: ProductType[] = []
        
        // Add product items by ID
        if (savedData.productIds) {
          const ids = savedData.productIds[cart.name] || []
          const productItems = ids
            .map(i => products.value.find(p => p.id === i))
            .filter((x): x is ProductType => !!x)
          items.push(...productItems)
        }
        
        // Add non-product items
        if (savedData.nonProducts) {
          const nonProductItems = savedData.nonProducts[cart.name] || []
          items.push(...nonProductItems)
        }
        
        cart.items = items
      })
    }

    // Load selected cart
    try {
      const name = await idbGet<string>(SELECTED_KEY, cartsStore)
      if (name) selectedCart.value = name
    } catch (error) {
      // Handle silently
    }

    // Set up persistence watchers only once globally
    if (!watchersInitialized) {
      console.log('Setting up watchers...')
      watchersInitialized = true
      
      watch(
        () => carts.value.map(c => c.items),
        async () => {
          const productIds: Record<string, string[]> = {}
          const nonProductItems: Record<string, ProductType[]> = {}
          
          carts.value.forEach(c => {
            const ids: string[] = []
            const nonProducts: ProductType[] = []
            
            c.items.forEach(item => {
              if (products.value.find(p => p.id === item.id)) {
                ids.push(item.id)
              } else {
                console.log('Non-product item:', item, 'Type:', typeof item, 'Keys:', Object.keys(item))
                // Create a plain JavaScript object from the reactive Vue object
                try {
                  // Unwrap the reactive object by converting to JSON and back
                  const plainItem = JSON.parse(JSON.stringify(item))
                  nonProducts.push(plainItem)
                } catch (err) {
                  console.error('Failed to unwrap non-product item:', err)
                }
              }
            })
            
            productIds[c.name] = ids
            nonProductItems[c.name] = nonProducts
          })
          
          console.log('Saving to IndexedDB:', { productIds, nonProductItems })
          
          try {
            await idbSet(IDS_KEY, productIds, cartsStore)
           // console.log('Successfully saved product IDs to IndexedDB')
          } catch (error) {
            console.error('Failed to save product IDs to IndexedDB:', error)
          }
          
          try {
            // Unwrap the entire object to ensure it's not reactive
            const plainNonProductItems = JSON.parse(JSON.stringify(nonProductItems))
            await idbSet(NON_PRODUCTS_KEY, plainNonProductItems, cartsStore)
           // console.log("Successfully saved non-product items to IndexedDB")
            
            // Verify data was actually saved
            const savedData = await idbGet<Record<string, ProductType[]>>(NON_PRODUCTS_KEY, cartsStore)
           // console.log("Verification - Read back non-product items:", savedData)
          } catch (error) {
            
            try {
              // Create a simplified object with only essential properties
              const simplifiedItems: Record<string, any[]> = {}
              Object.keys(nonProductItems).forEach(cartName => {
                simplifiedItems[cartName] = nonProductItems[cartName].map(item => ({
                  id: item.id,
                  name: item.name,
                  price: item.price,
                  qty: item.qty,
                  image: item.image,
                  description: item.description
                }))
              })
              
              await idbSet(NON_PRODUCTS_KEY, simplifiedItems, cartsStore)
              console.log("Successfully saved simplified non-product items")
            } catch (fallbackError) {
              console.error('Fallback save also failed:', fallbackError)
            }
          }
        },
        { deep: true }
      )

      watch(
        () => selectedCart.value,
        async name => {
          vm.selectedCart = name
          try {
            await idbSet(SELECTED_KEY, name, cartsStore)
            console.log('Successfully saved selected cart to IndexedDB')
          } catch (error) {
            console.error('Failed to save selected cart to IndexedDB:', error)
          }
        }
      )
    }
  })

  return {
    carts,
    selectedCart,
    selectedCartItems,
    selectCart,
    addToCart,
    deleteItem,
    saveForLater,
    bringBackToCart
  }
}
